import { db } from '$db/local/db';
import type { OutboxOperation, QuarantineRecord } from '$db/local/db';
import type { UUID } from '$domain/types';
import { getActiveUserId } from '$core/identity/active-user';
import { newId, nowIso } from './common';

export const QUARANTINE_MAX_OPS = 10_000;
export const QUARANTINE_MAX_BYTES = 5 * 1024 * 1024;

export interface QuarantineInput {
  entityType: string;
  entityId: string;
  op: 'upsert' | 'delete';
  payload: unknown;
  schemaVersion: number;
  reason: string;
  bytes?: number;
}

export async function quarantineOperation(
  input: QuarantineInput
): Promise<QuarantineRecord> {
  const now = nowIso();
  const record: QuarantineRecord = {
    id: newId(),
    userId: getActiveUserId(),
    entityType: input.entityType,
    entityId: input.entityId,
    op: input.op,
    payload: input.payload,
    schemaVersion: input.schemaVersion,
    reason: input.reason,
    error: input.reason, // Se usa 'reason' como mensaje de error por defecto
    createdAt: now,
    bytes: input.bytes ?? 0,
    status: 'quarantined',
    recoveredAt: null,
    discardedAt: null
  };
  await db.quarantine.add(record);
  await enforceQuarantineCapacity();
  return record;
}

export interface QuarantineCapacityResult {
  dropped: number;
  beforeOps: number;
  beforeBytes: number;
  afterOps: number;
  afterBytes: number;
}

export interface QuarantineCapacityOptions {
  userId?: string;
  maxOps?: number;
  maxBytes?: number;
}

/**
 * Cota FIFO de la cuarentena (M4.6).
 * Si el número de registros activos o sus bytes superan el límite, se
 * descartan físicamente los más antiguos. Borrado real, no lógico: la
 * cuarentena debe liberar espacio, no solo marcarlo.
 * Devuelve un resumen con cuántas ops se cayeron.
 */
export async function enforceQuarantineCapacity(
  options: QuarantineCapacityOptions = {}
): Promise<QuarantineCapacityResult> {
  const userId = options.userId ?? getActiveUserId();
  const maxOps = options.maxOps ?? QUARANTINE_MAX_OPS;
  const maxBytes = options.maxBytes ?? QUARANTINE_MAX_BYTES;

  return db.transaction('rw', db.quarantine, async () => {
    const all = await db.quarantine.where('userId').equals(userId).toArray();
    const active = all
      .filter((r) => r.status === 'quarantined')
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt));

    const beforeOps = active.length;
    const beforeBytes = active.reduce((s, r) => s + (r.bytes ?? 0), 0);

    if (beforeOps <= maxOps && beforeBytes <= maxBytes) {
      return {
        dropped: 0,
        beforeOps,
        beforeBytes,
        afterOps: beforeOps,
        afterBytes: beforeBytes
      };
    }

    let count = beforeOps;
    let bytes = beforeBytes;
    const toDelete: string[] = [];

    for (const rec of active) {
      if (count <= maxOps && bytes <= maxBytes) break;
      toDelete.push(rec.id);
      count -= 1;
      bytes -= rec.bytes ?? 0;
    }

    if (toDelete.length > 0) {
      await db.quarantine.bulkDelete(toDelete);
    }

    return {
      dropped: toDelete.length,
      beforeOps,
      beforeBytes,
      afterOps: count,
      afterBytes: bytes
    };
  });
}

/**
 * Reinserta una op en cuarentena en la outbox como pending y marca el
 * registro como recovered. Devuelve la op reinsertada, o null si el
 * registro no es recuperable (ya recovered/discarded, o sin payload).
 */
export async function requeueFromQuarantine(
  id: UUID
): Promise<OutboxOperation | null> {
  const results = await requeueManyFromQuarantine([id]);
  return results[0] ?? null;
}

/**
 * Versión batch de requeueFromQuarantine (M4.6 fix).
 * Una sola transacción rw de [outbox, quarantine]. Evita N disparos
 * individuales que saturan el auto-sync con operaciones masivas.
 * Devuelve las ops reinsertadas en el mismo orden que los ids válidos.
 */
export async function requeueManyFromQuarantine(
  ids: UUID[]
): Promise<OutboxOperation[]> {
  if (ids.length === 0) return [];

  const records = await db.quarantine.bulkGet(ids);
  const now = nowIso();
  const recoveredIds: string[] = [];
  const ops: OutboxOperation[] = [];

  for (const record of records) {
    if (!record) continue;
    if (record.status !== 'quarantined') continue;
    if (record.payload === null || record.payload === undefined) continue;

    ops.push({
      id: newId(),
      userId: record.userId,
      entityType: record.entityType,
      entityId: record.entityId,
      op: record.op,
      payload: record.payload,
      createdAt: now,
      updatedAt: now,
      idempotencyKey: newId(),
      attempts: 0,
      status: 'pending',
      bytes: record.bytes ?? 0,
      schemaVersion: record.schemaVersion ?? 1
    });
    recoveredIds.push(record.id);
  }

  if (ops.length === 0) return [];

  await db.transaction('rw', [db.outbox, db.quarantine], async () => {
    await db.outbox.bulkAdd(ops);
    for (const id of recoveredIds) {
      await db.quarantine.update(id, {
        status: 'recovered',
        recoveredAt: now
      });
    }
  });

  return ops;
}

/**
 * Marca como descartados varios registros en una sola transacción (M4.6 fix).
 * Devuelve cuántos se actualizaron efectivamente.
 */
export async function markManyQuarantineDiscarded(
  ids: UUID[]
): Promise<number> {
  if (ids.length === 0) return 0;
  const now = nowIso();
  let count = 0;
  await db.transaction('rw', db.quarantine, async () => {
    for (const id of ids) {
      const rec = await db.quarantine.get(id);
      if (!rec || rec.status !== 'quarantined') continue;
      await db.quarantine.update(id, {
        status: 'discarded',
        discardedAt: now
      });
      count += 1;
    }
  });
  return count;
}

export async function listQuarantine(
  userId = getActiveUserId()
): Promise<QuarantineRecord[]> {
  const rows = await db.quarantine.where('userId').equals(userId).toArray();
  return rows.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

export async function listActiveQuarantine(
  userId = getActiveUserId()
): Promise<QuarantineRecord[]> {
  const all = await listQuarantine(userId);
  return all.filter((r) => r.status === 'quarantined');
}

export async function markQuarantineRecovered(id: UUID): Promise<void> {
  await db.quarantine.update(id, {
    status: 'recovered',
    recoveredAt: nowIso()
  });
}

export async function markQuarantineDiscarded(id: UUID): Promise<void> {
  await db.quarantine.update(id, {
    status: 'discarded',
    discardedAt: nowIso()
  });
}

export interface QuarantineStats {
  count: number;
  bytes: number;
}

export async function getQuarantineStats(
  userId = getActiveUserId()
): Promise<QuarantineStats> {
  const active = await listActiveQuarantine(userId);
  return {
    count: active.length,
    bytes: active.reduce((sum, r) => sum + (r.bytes ?? 0), 0)
  };
}